import { useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FileSpreadsheet, Plus, ArrowRight } from "lucide-react";
import { TenantLayout, useTenant } from "@/components/tenant/tenant-layout";
import { useAuth } from "@/lib/auth/auth-provider";
import { useRole } from "@/lib/auth/use-role";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  PageHeader,
  Spinner,
} from "@/components/ui/primitives";
import {
  createFormAdvFiling,
  createFormPfFiling,
  listFilings,
  type FilingFormType,
  type FilingRow,
} from "@/lib/api/filings";
import { FORM_LABEL, FILING_STATUS_LABEL, FILING_STATUS_TONE } from "@/lib/filing-labels";
import { taskUrgency } from "@argus/engine";
import { URGENCY_LABEL, URGENCY_TONE } from "@/lib/compliance-labels";

const TODAY = new Date().toISOString().slice(0, 10);

/**
 * Default a filing period to the most recently completed fiscal year. Form PF is
 * due within 120 days of fiscal year-end; the Form ADV annual updating amendment
 * within 90 days.
 */
function defaultPeriod(formType: FilingFormType): { period: string; fye: string; due: string } {
  const y = new Date().getUTCFullYear() - 1;
  const fye = `${y}-12-31`;
  const dueDays = formType === "form_adv" ? 90 : 120;
  const due = new Date(Date.UTC(y + 1, 11, 31 + dueDays)).toISOString().slice(0, 10);
  return { period: `FY${y}`, fye, due };
}

function FilingsInner() {
  const tenant = useTenant();
  const { user } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { canWrite, role } = useRole(tenant.id);
  const canPrepare = canWrite || role === "reviewer";

  const [showNew, setShowNew] = useState(false);
  const [formType, setFormType] = useState<FilingFormType>("form_pf");
  const def = defaultPeriod("form_pf");
  const [period, setPeriod] = useState(def.period);
  const [fye, setFye] = useState(def.fye);
  const [due, setDue] = useState(def.due);

  function pickForm(ft: FilingFormType) {
    setFormType(ft);
    setDue(defaultPeriod(ft).due);
  }

  const { data: filings, isLoading } = useQuery({
    queryKey: ["filings", tenant.id],
    queryFn: () => listFilings(tenant.id),
  });

  const create = useMutation({
    mutationFn: () => {
      if (!user) throw new Error("Not signed in.");
      const args = {
        tenantId: tenant.id,
        periodLabel: period.trim(),
        fiscalYearEnd: fye || null,
        dueDate: due || null,
        userId: user.id,
      };
      return formType === "form_adv" ? createFormAdvFiling(args) : createFormPfFiling(args);
    },
    onSuccess: (filing) => {
      void qc.invalidateQueries({ queryKey: ["filings", tenant.id] });
      void navigate({
        to: "/t/$slug/filings/$filingId",
        params: { slug: tenant.slug, filingId: filing.id },
      });
    },
  });

  return (
    <>
      <PageHeader
        title="Regulatory filings"
        subtitle="Prepare deadline-driven filings — Argus auto-populates what it knows, flags what it doesn't, and drafts the rest for your review."
        actions={
          canPrepare ? (
            <Button variant="primary" onClick={() => setShowNew((s) => !s)}>
              <Plus className="size-4" /> New filing
            </Button>
          ) : undefined
        }
      />

      {showNew && canPrepare && (
        <Card className="space-y-4 p-4">
          <Field label="Form">
            <div className="flex gap-2">
              {(["form_pf", "form_adv"] as FilingFormType[]).map((ft) => (
                <button
                  key={ft}
                  type="button"
                  onClick={() => pickForm(ft)}
                  className={`rounded-md border px-3 py-1.5 text-sm transition ${
                    formType === ft
                      ? "border-brand-600/60 bg-brand-950/30 text-brand-200"
                      : "border-neutral-800 bg-neutral-950 text-neutral-400 hover:border-neutral-700"
                  }`}
                >
                  {FORM_LABEL[ft]}
                </button>
              ))}
            </div>
          </Field>
          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Reporting period">
              <input
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                placeholder="FY2025"
                className="w-full rounded-md border border-neutral-800 bg-neutral-950 px-2.5 py-1.5 text-sm text-neutral-200 placeholder:text-neutral-600"
              />
            </Field>
            <Field label="Fiscal year-end">
              <input
                type="date"
                value={fye}
                onChange={(e) => setFye(e.target.value)}
                className="w-full rounded-md border border-neutral-800 bg-neutral-950 px-2.5 py-1.5 text-sm text-neutral-200"
              />
            </Field>
            <Field label="Filing due">
              <input
                type="date"
                value={due}
                onChange={(e) => setDue(e.target.value)}
                className="w-full rounded-md border border-neutral-800 bg-neutral-950 px-2.5 py-1.5 text-sm text-neutral-200"
              />
            </Field>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="primary"
              disabled={!period.trim() || create.isPending}
              onClick={() => create.mutate()}
            >
              {create.isPending ? (
                <Spinner className="size-4" />
              ) : (
                <FileSpreadsheet className="size-4" />
              )}
              Create &amp; auto-populate
            </Button>
            <Button variant="ghost" onClick={() => setShowNew(false)}>
              Cancel
            </Button>
            {create.error && <span className="text-xs text-red-300">{create.error.message}</span>}
          </div>
          <p className="text-[11px] text-neutral-500">
            Creates a {FORM_LABEL[formType]} draft for the period and fills every field Argus can
            derive from your funds, LPs, and commitments. Existing periods open in place (no
            duplicates).
          </p>
        </Card>
      )}

      {isLoading ? (
        <p className="text-sm text-neutral-500">Loading filings…</p>
      ) : (filings ?? []).length === 0 ? (
        <EmptyState
          icon={<FileSpreadsheet className="size-8" />}
          title="No filings yet"
          description={
            canPrepare
              ? "Start a Form PF or Form ADV for your most recent fiscal year — Argus pre-fills what it knows and flags the gaps."
              : "No regulatory filings have been prepared for this firm yet."
          }
          action={
            canPrepare ? (
              <Button variant="primary" onClick={() => setShowNew(true)}>
                <Plus className="size-4" /> New filing
              </Button>
            ) : undefined
          }
        />
      ) : (
        <div className="space-y-2">
          {(filings ?? []).map((f) => (
            <FilingCard key={f.id} filing={f} slug={tenant.slug} />
          ))}
        </div>
      )}
    </>
  );
}

function FilingCard({ filing, slug }: { filing: FilingRow; slug: string }) {
  const urgency = taskUrgency(
    filing.due_date,
    45,
    filing.status === "filed" ? "completed" : "open",
    TODAY,
  );
  return (
    <Link to="/t/$slug/filings/$filingId" params={{ slug, filingId: filing.id }} className="block">
      <Card className="flex items-center gap-4 px-4 py-3 transition hover:border-neutral-700 hover:bg-neutral-900/70">
        <div className="flex size-9 shrink-0 items-center justify-center rounded-md bg-neutral-800/70 text-neutral-400">
          <FileSpreadsheet className="size-4.5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate text-sm font-medium text-neutral-100">{filing.title}</span>
            <Badge tone="neutral">{FORM_LABEL[filing.form_type]}</Badge>
          </div>
          <div className="mt-0.5 text-xs text-neutral-500">
            {filing.due_date ? (
              <>
                Due <span className="tabular-nums text-neutral-400">{filing.due_date}</span>
              </>
            ) : (
              "No due date"
            )}
            {filing.period_label ? ` · ${filing.period_label}` : ""}
          </div>
        </div>
        <Badge tone={URGENCY_TONE[urgency]}>{URGENCY_LABEL[urgency]}</Badge>
        <Badge tone={FILING_STATUS_TONE[filing.status]}>{FILING_STATUS_LABEL[filing.status]}</Badge>
        <ArrowRight className="size-4 text-neutral-600" />
      </Card>
    </Link>
  );
}

export function FilingsListPage() {
  return (
    <TenantLayout>
      <FilingsInner />
    </TenantLayout>
  );
}
